import { BASE_CANONICAL_ORIGIN } from './constants';
import { buildAverageGradePage, buildGradingScaleIndexPage, buildQuestionCountPage } from './gradingScalePages';
import { GRADING_SCALE_SIZES } from './gradingScaleSizes';
import { buildCumulativeGpaPage, buildMissCalculatorPage, buildSemesterGpaPage, buildWeightedGpaPage, buildWeightedVsUnweightedPage } from './gpaPages';
import { buildForSchoolsPage } from './forSchoolsPage';
import {
  buildBlogIndexPage,
  buildCurveGuidePost,
  buildGradeFasterPost,
  buildGradingSystemsPost,
  buildHighSchoolVsCollegePost,
} from './blogPages';
import { buildGpaToLetterPage, buildLetterToGpaPage } from './gpaLetterPages';
import { buildCollegeGpaPage, buildHighSchoolGpaPage } from './schoolGpaPages';
import { buildFinalPlannerPage } from './finalPlannerPage';
import { buildCgpaUniversityPages } from './cgpaUniversityPages';
import { COMPETITOR_GAP_PAGES } from './competitorGapPages';
import { applySeoExtras } from './seoExtras';
import { applyOnPageSeo } from './onPageSeo';
import { SCALE_PLUS_MINUS, SCALE_STANDARD, applyCurve, calculateTestGrade, computeStats, percentToLetter, roundTo } from '../utils/academicMath';

/**
 * Registry for the dedicated academic calculators that are NOT programmatic variants of the
 * weighted-grade tool. Each page has its own interactive tool, its own worked example (generated
 * from the same math the tool uses) and its own FAQ, so every URL serves one distinct search intent.
 */

export type CoreToolPageSlug = 'test-grade-calculator' | 'grade-curve-calculator' | 'letter-grade-calculator';

/**
 * Core pages plus the generated ones: 'average-grade-calculator', 'grading-scale' and
 * 'grading-scale/N-questions'. The slug is also the folder the page is prerendered into.
 */
export type ToolPageSlug = string;

export interface ToolPageFaq {
  question: string;
  answer: string;
}

export interface ToolPageSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

/** A simple data table rendered as a real <table> (good for lookups and snippet-style answers). */
export interface ToolPageTable {
  heading: string;
  intro?: string;
  columns: string[];
  rows: string[][];
}

export interface ToolPageRelated {
  path: string;
  label: string;
  blurb: string;
}

export interface ToolPageEntry {
  slug: ToolPageSlug;
  path: string;
  navLabel: string;
  h1: string;
  title: string;
  metaDescription: string;
  badge: string;
  intro: string;
  keywords: string[];
  featureList: string[];
  sections: ToolPageSection[];
  /** Use 'WebPage' for pages without an interactive tool (guides). Defaults to WebApplication. */
  schemaType?: 'WebApplication' | 'WebPage';
  /** Optional lookup tables shown right after the tool. */
  lookupTables?: ToolPageTable[];
  example: {
    heading: string;
    scenario: string;
    rows: { label: string; value: string }[];
    takeaway: string;
  };
  faqs: ToolPageFaq[];
  related: ToolPageRelated[];
  /** Absolute URL of this page's own 1200x630 share card (generated by scripts/generate_og_cards.py). */
  ogImage?: string;
  /** Set on /grading-scale/N-questions/ pages: the test size the chart is built for. */
  questions?: number;
  /** Optional heading for the FAQ block (otherwise PAGE_FAQ_HEADINGS or the default is used). */
  faqHeading?: { eyebrow: string; title: string; description: string; ctaText: string };
}

/* ---------- worked examples are computed, never typed by hand ---------- */

const testEx = calculateTestGrade({ earned: 42, possible: 50, bonus: 2 });
const testExLetter = percentToLetter(testEx.percent, SCALE_STANDARD).letter;
const testExPlain = calculateTestGrade({ earned: 42, possible: 50 });

const curveRaw = [55, 62, 70, 78, 85];
const curveStatsBefore = computeStats(curveRaw);
const curveFlat = applyCurve(curveRaw, { method: 'flat', flatPoints: 5 });
const curveTop = applyCurve(curveRaw, { method: 'scale-top' });
const curveSqrt = applyCurve(curveRaw, { method: 'sqrt' });
const curveTarget = applyCurve(curveRaw, { method: 'linear-target', targetAverage: 78, capAtMax: false });
const curveBell = applyCurve(curveRaw, { method: 'bell', bellMean: 75, bellSd: 10 });

const fmtList = (xs: number[]) => xs.map((x) => roundTo(x, 1)).join(', ');

const CORE_PAGES: Record<CoreToolPageSlug, ToolPageEntry> = {
  'test-grade-calculator': {
    slug: 'test-grade-calculator',
    path: '/test-grade-calculator/',
    navLabel: 'Test Grade',
    h1: 'Test Grade Calculator',
    title: 'Test Grade Calculator — Score Percentage & Letter Grade',
    metaDescription:
      'Free test score percentage calculator: enter points earned and points possible to get the percentage and letter grade, with bonus points and penalties.',
    badge: 'Single test or quiz',
    intro:
      'Type the points you earned and the points the test was worth. You get the percentage, the letter grade on your chosen scale, and the points you would have needed for the next grade up.',
    keywords: [
      'test grade calculator',
      'quiz grade calculator',
      'test score percentage calculator',
      'how to calculate test percentage',
      'how to calculate grade percentage',
      'points possible to percentage',
      'wrong answers to percentage',
      'points earned out of possible',
      'extra credit test grade',
    ],
    featureList: [
      'Percentage and letter grade from points earned and points possible',
      'Bonus points and late-penalty adjustments',
      'Points needed for A, B, C and D on any test size',
      'Standard and plus/minus letter scales',
    ],
    sections: [
      {
        heading: 'How a test grade is calculated',
        paragraphs: [
          'A test grade is the share of the available points you earned: percentage = points earned ÷ points possible × 100. If a test is worth 50 points and you scored 42, the result is 42 ÷ 50 = 0.84, or 84%.',
          'The letter comes from the grading scale your teacher or school uses, not from the math itself. On the common 10-point scale 84% is a B; on a plus/minus scale it is a B. The same 89.6% can be a B+ on one scale and a B on another, which is why this calculator lets you switch scales.',
        ],
      },
      {
        heading: 'Extra credit and late penalties',
        paragraphs: [
          'Most teachers add extra-credit points to the points you earned and leave the points possible unchanged, so a student can finish above 100%. Others cap the result at 100%. This calculator adds bonus points to the numerator and shows the uncapped percentage, so you can see both outcomes and apply your teacher’s rule.',
          'Penalties work the other way. A late-work deduction of 5 points is subtracted from the points earned before dividing. If your teacher takes a percentage off instead, subtract that percentage from the final result.',
        ],
      },
      {
        heading: 'What score do I need on the next test?',
        paragraphs: [
          'Multiply the target percentage by the points possible. To reach 90% on a 35-point quiz you need 0.90 × 35 = 31.5 points. Because teachers award whole or half points, you would round up to 32 in practice. The “points needed” table under the calculator does this for every grade boundary at once.',
          'A single test is only one part of a course grade. To see how it moves your overall average, enter it as an assessment in the weighted grade calculator, or work backwards from your target with the final exam calculator.',
        ],
      },
    ],
    example: {
      heading: 'Worked example: 42 out of 50 with 2 bonus points',
      scenario:
        'A student earns 42 points on a 50-point chemistry test and receives 2 bonus points for a correct extra-credit question.',
      rows: [
        { label: 'Percentage without bonus', value: `${roundTo(testExPlain.percent, 1)}%` },
        { label: 'Points after bonus', value: `${testEx.adjustedEarned} of 50` },
        { label: 'Percentage with bonus', value: `${roundTo(testEx.percent, 1)}%` },
        { label: 'Letter grade (10-point scale)', value: testExLetter },
      ],
      takeaway:
        'Two bonus points lift the score from 84% to 88%, which is still a B. One more point (45 of 50) would have reached the 90% cutoff for an A.',
    },
    faqs: [
      {
        question: 'How do I calculate my test grade?',
        answer:
          'Divide the points you earned by the points the test was worth and multiply by 100. For example, 42 out of 50 is 84%. Then compare the percentage with your teacher’s grading scale to get the letter.',
      },
      {
        question: 'How do I add extra credit to a test grade?',
        answer:
          'Add the bonus points to the points you earned, keep the points possible the same, and divide. 42 + 2 bonus on a 50-point test is 44 ÷ 50 = 88%. Some teachers cap the result at 100%, so check your syllabus.',
      },
      {
        question: 'Can a test grade be higher than 100%?',
        answer:
          'Yes, if extra credit is added without raising the points possible. Whether the gradebook keeps the value above 100% or caps it is a teacher or school policy.',
      },
      {
        question: 'How many questions can I miss and still get a B?',
        answer:
          'Multiply the points possible by 0.20 and round down. On a 25-question test worth one point each you can miss up to 5 questions and keep 80%, the usual B cutoff. The points-needed table shows this for any test size.',
      },
      {
        question: 'Is this the same as the Quick Grade chart?',
        answer:
          'The Quick Grade chart lists the score for every possible number of wrong answers so a teacher can grade a whole stack from one printed sheet. This calculator grades one result at a time and handles bonus points and penalties.',
      },
      {
        question: 'What is a passing grade on a test?',
        answer:
          'On the common 10-point scale 60% is the lowest passing mark (a D) and anything under 60% is an F. Some schools and programs set the pass mark at 70%, and some instructors set their own cutoff for a single test, so the syllabus decides.',
      },
      {
        question: 'How do I apply a late penalty to a test score?',
        answer:
          'If the penalty is in points, subtract it from the points earned before dividing: 42 earned minus a 5-point penalty is 37 out of 50, or 74%. If the penalty is a percentage, calculate the score first and then take that many percentage points off the result.',
      },
      {
        question: 'How do I calculate a test score percentage?',
        answer:
          'Divide the points you earned by the points possible and multiply by 100. For example, 42 out of 50 is 42 ÷ 50 × 100 = 84%. Add any bonus points to the points earned first, and subtract a points penalty before dividing.',
      },
      {
        question: 'What percentage is X out of Y questions?',
        answer:
          'Divide X by Y and multiply by 100. 18 out of 24 questions is 75%, 33 out of 40 is 82.5% and 47 out of 60 is 78.3%. Enter your own numbers above to get any other result and its letter grade.',
      },
      {
        question: 'How do I convert wrong answers to a percentage?',
        answer:
          'Subtract the wrong answers from the total, divide by the total and multiply by 100. Missing 4 questions on a 25-question test gives 21 ÷ 25 × 100 = 84%. Each question on that test is worth 4 percentage points.',
      },
      {
        question: 'How do I turn a score out of 20, 25 or 30 into a percentage?',
        answer:
          'Divide the points you earned by the points possible and multiply by 100. A 17 out of 20 is 85%, a 22 out of 25 is 88%, and a 26 out of 30 is 86.7%. The calculator does this for any test size and shows the letter grade next to it.',
      },
    ],
    related: [
      { path: '/', label: 'Quick Grade chart', blurb: 'Printable score for every number of wrong answers.' },
      { path: '/grade-calculator/', label: 'Weighted grade calculator', blurb: 'Combine tests, homework and projects into a course grade.' },
      { path: '/final-exam-grade-calculator/', label: 'Final exam calculator', blurb: 'Find the exam score you need for your target grade.' },
      { path: '/letter-grade-calculator/', label: 'Letter grade calculator', blurb: 'Percent, letter and GPA points on common scales.' },
    ],
  },

  'grade-curve-calculator': {
    slug: 'grade-curve-calculator',
    path: '/grade-curve-calculator/',
    navLabel: 'Grade Curve',
    h1: 'Grade Curve Calculator',
    title: 'Grade Curve Calculator — 5 Curving Methods Compared',
    metaDescription:
      'Paste class scores and curve them five ways: flat points, top score to 100, square root, target average or bell curve. Before-and-after letters included.',
    badge: 'For teachers and TAs',
    intro:
      'Paste the raw scores, choose a curving method and compare before and after: every score, the class mean and median, and the letter grade each student ends up with.',
    keywords: [
      'grade curve calculator',
      'test curve calculator',
      'square root curve',
      'curve grades calculator',
      'how to curve a test',
    ],
    featureList: [
      'Flat-points, top-score, square-root, target-average and bell curves',
      'Before and after table with class mean and median',
      'Standard and plus/minus letter grades for each score',
      'Optional cap at 100 and printable results',
    ],
    sections: [
      {
        heading: 'The five curving methods',
        paragraphs: [
          'Curving means adjusting raw scores with a rule that is applied to everyone. These are the five most common rules, and the calculator applies each exactly as written.',
        ],
        bullets: [
          'Flat points: curved score = raw score + k. Everyone gains the same number of points, so gaps between students do not change.',
          'Top score to 100: curved score = raw score + (100 − highest raw score). The best score becomes 100 and everyone shifts up by the same amount.',
          'Square-root curve: curved score = 10 × √raw score (for a 100-point test). Low scores gain more than high scores, so the spread narrows.',
          'Target average: curved score = raw score + (target mean − current mean). The class average lands exactly on the value you choose.',
          'Bell curve: each score is converted to a z-score, (score − class mean) ÷ standard deviation, then rebuilt on the average and spread you choose. Rank order never changes, but unlike the other four methods a bell curve can move a score down as well as up.',
        ],
      },
      {
        heading: 'Which method fits which situation',
        paragraphs: [
          'Use flat points when a single question was ambiguous or a topic was not taught: it is easy to explain and fair to every student. Use top score to 100 when the test was harder than intended and no one reached full marks.',
          'The square-root curve helps most when many scores are low, but it changes the ranking of gaps: a 36 becomes 60 (+24) while an 81 becomes 90 (+9). Use the target-average method when your department expects a particular class mean, for example 78%.',
        ],
      },
      {
        heading: 'Things to check before you publish curved grades',
        paragraphs: [
          'Capping at 100 compresses the top of the class: two students at 97 and 99 can both become 100 after a flat +5 curve. Turn the cap off to see the uncapped values, then decide what your gradebook should record.',
          'A curve is an instructor decision. Students use this tool to estimate where a curve might put them; the official result is whatever your instructor or syllabus applies.',
        ],
      },
    ],
    example: {
      heading: 'Worked example: five scores, five methods',
      scenario: `A small quiz returns the raw scores ${curveRaw.join(', ')} (mean ${roundTo(curveStatsBefore.mean, 1)}, median ${roundTo(curveStatsBefore.median, 1)}).`,
      rows: [
        { label: 'Flat +5', value: fmtList(curveFlat) },
        { label: 'Top score to 100 (+15)', value: fmtList(curveTop) },
        { label: 'Square-root curve', value: fmtList(curveSqrt) },
        { label: 'Target average 78', value: fmtList(curveTarget) },
        { label: 'Bell curve (average 75, spread 10)', value: fmtList(curveBell) },
      ],
      takeaway:
        'The target-average curve adds 8 points to everyone so the mean moves from 70 to 78. The square-root curve lifts the lowest score the most (55 becomes 74.2) and the highest the least (85 becomes 92.2).',
    },
    faqs: [
      {
        question: 'How do you curve a test?',
        answer:
          'Pick one rule and apply it to every score: add the same points to everyone, raise the top score to 100, take the square root, shift scores so the class average hits a target, or rescale the class onto a bell curve. Paste the scores above to compare the results.',
      },
      {
        question: 'What is a square root curve?',
        answer:
          'For a 100-point test the curved score is 10 times the square root of the raw score. A 64 becomes 80 and a 49 becomes 70. Lower scores are raised more than higher ones.',
      },
      {
        question: 'Does curving lower anyone’s grade?',
        answer:
          'Four of the five methods here never lower a score: flat points, top score to 100, square root and target average only move scores up or keep them level. The bell curve is different by design: it rescales the whole class onto a chosen average and spread, so a score far above the class mean can come down. Check the before-and-after table before you apply it.',
      },
      {
        question: 'Should curved scores be capped at 100?',
        answer:
          'That is a policy choice. Many instructors cap at 100 so no score exceeds the maximum. The cap is on by default here and can be switched off to see raw curved values.',
      },
      {
        question: 'Can students curve their own grade?',
        answer:
          'Only the instructor decides whether and how a curve is applied. Students can paste the class scores they know about to estimate the effect, but the official grade is the instructor’s.',
      },
      {
        question: 'What is the difference between these curves and a bell curve?',
        answer:
          'Flat points, top score, square root and target average move every score by one fixed rule, so the gaps between students stay recognisable. The bell curve method rescales scores by their distance from the class mean (the z-score), then rebuilds them on the average and spread you set, so the shape of the class distribution changes while the rank order stays the same. Ask your instructor which policy applies.',
      },
      {
        question: 'How does the bell curve method work?',
        answer:
          'Each raw score becomes a z-score: (score − class mean) ÷ standard deviation. The curved score is then bell average + z × bell spread. With the defaults (average 75, spread 10), a student exactly one standard deviation above the class mean receives 85, and a student one below receives 65, whatever the raw scores were.',
      },
      {
        question: 'How do I find the class mean and median before curving?',
        answer:
          'The mean is the sum of all scores divided by the number of scores, and the median is the middle score once they are sorted. For 55, 62, 70, 78 and 85 the sum is 350, so the mean is 70 and the median is 70. The calculator shows both before and after the curve.',
      },
      {
        question: 'How many points should I add to curve a test?',
        answer:
          'There is no single right number, because it depends on your goal. To bring the class average from 70 up to 78 you add 8 points, and to make an 85 the top score of 100 you add 15. Try each method on your own scores and compare the results before deciding.',
      },
    ],
    related: [
      { path: '/test-grade-calculator/', label: 'Test grade calculator', blurb: 'Percentage and letter for one test, with bonus points.' },
      { path: '/letter-grade-calculator/', label: 'Letter grade calculator', blurb: 'See the cutoffs behind each letter.' },
      { path: '/grade-calculator/', label: 'Weighted grade calculator', blurb: 'Turn curved scores into a course grade.' },
      { path: '/', label: 'Quick Grade chart', blurb: 'Print a score chart for a stack of tests.' },
    ],
  },

  'letter-grade-calculator': {
    slug: 'letter-grade-calculator',
    path: '/letter-grade-calculator/',
    navLabel: 'Letter Grade',
    h1: 'Letter Grade Calculator',
    title: 'Letter Grade Calculator — Percent to Letter & GPA Points',
    metaDescription:
      'Convert any percentage to a letter grade and 4.0 GPA points. See what GPA a 73, 85 or 90 is, plus the cutoffs for each letter on A–F and plus/minus scales.',
    badge: 'Percent ↔ letter',
    intro:
      'Enter a percentage to see its letter grade and GPA points, or pick a letter to see the percentage range and midpoint. Switch between the standard A–F scale and the common plus/minus scale.',
    keywords: [
      'letter grade calculator',
      'percent to letter grade',
      'percentage to gpa',
      'grade scale chart',
      'a-f grading scale',
      'letter grade percentage chart',
      'what percentage is an a grade',
    ],
    featureList: [
      'Percent to letter grade and 4.0 GPA points',
      'Letter to percentage range and midpoint',
      'Standard and plus/minus scales with full cutoff tables',
      'Printable grade scale reference',
    ],
    sections: [
      {
        heading: 'The two scales most US classes use',
        paragraphs: [
          'The standard scale has five letters with 10-point bands: A 90–100, B 80–89, C 70–79, D 60–69, F below 60. The plus/minus scale splits each band into thirds: for example B+ starts at 87, B at 83 and B- at 80.',
          'Both tables below are generated from the same data the calculator uses, so what you read is what you get. The cutoffs shown are the most common ones, not a universal law.',
        ],
      },
      {
        heading: 'Why your school’s cutoffs may differ',
        paragraphs: [
          'Schools set their own scales. Some start an A at 93 instead of 90, some treat 89.5 as 90 after rounding, and some give an A+ a higher value than an A (4.3 instead of 4.0). If the syllabus lists cutoffs, those override everything on this page.',
          'Two practical checks avoid surprises: ask whether the percentage is rounded before the letter is assigned, and whether the plus/minus scale applies to final course grades or only to individual assignments.',
        ],
      },
      {
        heading: 'What GPA is a 73%? Common percentages in GPA points',
        paragraphs: [
          'A 73% is a C on both common scales, worth 2.0 on the 4.0 scale. On the plus/minus scale it is the lowest C: 70 to 72.99 is a C- (1.7) and 77 starts a C+ (2.3).',
          'The same logic applies to other scores. A 90% is an A (4.0) on the standard scale or an A- (3.7) on the plus/minus scale, and an 85% is a B (3.0) on both. Use the calculator above for any other percentage.',
        ],
      },
      {
        heading: 'From letter grade to GPA points',
        paragraphs: [
          'GPA points come from the letter, not the percentage: on the common 4.0 scale A is 4.0, A- is 3.7, B+ is 3.3 and so on down to F at 0.0. A course’s contribution to GPA is its points multiplied by its credit hours.',
          'To combine several courses, take the letters you calculated here to the GPA calculator. To convert a CGPA from a 10-point system into a percentage, use the CGPA to percentage converter, which follows each university’s own formula.',
        ],
      },
    ],
    example: {
      heading: 'Worked example: 89.5% on both scales',
      scenario: 'A final course average of 89.5% earns a different letter and different GPA points depending on which scale the school uses.',
      rows: [
        { label: 'Standard A–F scale', value: `${percentToLetter(89.5, SCALE_STANDARD).letter} (${percentToLetter(89.5, SCALE_STANDARD).gpa.toFixed(1)} GPA points)` },
        { label: 'Plus/minus scale', value: `${percentToLetter(89.5, SCALE_PLUS_MINUS).letter} (${percentToLetter(89.5, SCALE_PLUS_MINUS).gpa.toFixed(1)} GPA points)` },
        { label: 'Percentage points short of an A (standard) or A- (plus/minus)', value: `${roundTo(90 - 89.5, 1)}` },
      ],
      takeaway:
        'The same 89.5% is a B (3.0) on the standard scale and a B+ (3.3) on the plus/minus scale, and half a point from the next letter on both. Whether it rounds up to 90 depends on your school’s rounding rule.',
    },
    faqs: [
      {
        question: 'What GPA is a 73%?',
        answer:
          'A 73% is a C, which is 2.0 GPA points on the 4.0 scale. On the plus/minus scale it is the lowest C (2.0), with 70 to 72.99 a C- (1.7). Your school’s scale decides, so check the syllabus.',
      },
      {
        question: 'What percentage is an A, B, C, D and F?',
        answer:
          'On the common 10-point scale an A is 90–100%, a B is 80–89%, a C is 70–79%, a D is 60–69% and anything below 60% is an F. Some schools use 93, 83, 73 and 63 as the lower limits instead.',
      },
      {
        question: 'What is an A- on the percentage scale?',
        answer:
          'On the common plus/minus scale an A- covers 90 to 92.99%, an A covers 93 to 96.99% and an A+ starts at 97%. Check your syllabus because schools differ.',
      },
      {
        question: 'How do I convert a letter grade to a percentage?',
        answer:
          'Use the range for that letter on your scale. If you need a single number, the midpoint is the usual choice: 85 for a B on the standard scale. The calculator shows both.',
      },
      {
        question: 'Is an A+ worth more than an A in GPA?',
        answer:
          'On most 4.0 scales both are 4.0. Some schools use 4.3 for an A+. Your registrar’s scale decides.',
      },
      {
        question: 'Does 89.5% round up to an A?',
        answer:
          'Only if your instructor rounds before assigning the letter. This calculator does not round for you: it uses the exact value you type, so you can see which side of the cutoff you are on.',
      },
      {
        question: 'What is the lowest passing letter grade?',
        answer:
          'On most scales a D (60%) is the lowest passing grade, and a D- on the plus/minus scale. Many colleges require a C or better for major courses and prerequisites, so a D may pass the course without counting toward your program.',
      },
      {
        question: 'What percentage do you need to get an A?',
        answer:
          'Most classes need 90% for an A, and some schools require 93%. On a plus/minus scale 93% is a plain A and 90% is an A-. Check your syllabus, because the cutoff and the rounding rule are set by your school or instructor.',
      },
      {
        question: 'How does the A to F grading scale work?',
        answer:
          'The A to F grading scale groups percentages into letters: A is 90–100%, B is 80–89%, C is 70–79%, D is 60–69% and F is below 60%. Each letter maps to GPA points from 4.0 for an A down to 0.0 for an F.',
      },
      {
        question: 'Is there a letter grade percentage chart I can print?',
        answer:
          'Yes. The standard and plus/minus cutoff tables on this page are a letter grade percentage chart you can print. For a chart that lists every possible score on a test of a given size, use the grading scale charts.',
      },
      {
        question: 'What percentage is a B+?',
        answer:
          'On the common plus/minus scale a B+ covers 87% to 89.99%, a B covers 83% to 86.99% and a B- covers 80% to 82.99%. On the standard A–F scale there is no B+, so the same 88% is simply a B.',
      },
      {
        question: 'How many GPA points does each letter grade earn?',
        answer:
          'On the common 4.0 scale an A is 4.0, A- 3.7, B+ 3.3, B 3.0, B- 2.7, C+ 2.3, C 2.0, C- 1.7, D+ 1.3, D 1.0, D- 0.7 and F 0.0. Your registrar may use a different table, so check the official scale.',
      },
    ],
    related: [
      { path: '/gpa-calculator/', label: 'GPA calculator', blurb: 'Combine letters and credits into semester and cumulative GPA.' },
      { path: '/cgpa-to-percentage-calculator/', label: 'CGPA to percentage', blurb: '10-point, 5-point and 4-point conversions by university.' },
      { path: '/gpa-to-letter-grade/', label: 'GPA to letter grade', blurb: 'Convert a GPA back to its letter grade.' },
      { path: '/letter-grade-to-gpa/', label: 'Letter grade to GPA', blurb: 'What each letter is worth in grade points.' },
      { path: '/test-grade-calculator/', label: 'Test grade calculator', blurb: 'Get the percentage first, then the letter.' },
      { path: '/grade-calculator/', label: 'Weighted grade calculator', blurb: 'Build the course percentage from every assessment.' },
    ],
  },
};

// Cross-links from the core pages to the new page types (internal linking for the grading-scale hub).
CORE_PAGES['test-grade-calculator'].related.push({ path: '/grading-scale/', label: 'Grading scale charts', blurb: 'Score charts for tests of 5 to 100 questions.' });
CORE_PAGES['letter-grade-calculator'].related.push({ path: '/grading-scale/', label: 'Grading scale charts', blurb: 'Percent and letter for every score on 5 to 100 questions.' });
CORE_PAGES['grade-curve-calculator'].related.push({ path: '/average-grade-calculator/', label: 'Average grade calculator', blurb: 'Mean and median for a list of scores.' });

// Quick-answer content added by the SEO fix kit (search-intent audit): every figure is computed from the shared scales.
{
  const pct = (n: number) => roundTo(n, 1);
  const lookupRows: string[][] = [];
  for (let p = 100; p >= 60; p--) {
    const std = percentToLetter(p, SCALE_STANDARD);
    const pm = percentToLetter(p, SCALE_PLUS_MINUS);
    lookupRows.push([`${p}%`, std.letter, pm.letter, pm.gpa.toFixed(1)]);
  }
  lookupRows.push(['Below 60%', 'F', 'F', '0.0']);
  const p75std = percentToLetter(75, SCALE_STANDARD);
  const p75pm = percentToLetter(75, SCALE_PLUS_MINUS);
  CORE_PAGES['letter-grade-calculator'].lookupTables = [
    {
      heading: 'Percent to letter grade chart',
      intro: 'Find any whole percentage from 60 to 100. The A–F column uses the 90/80/70/60 scale; the plus/minus column uses the 97/93/90/87/83/80/77/73/70/67/63/60 scale with its GPA points.',
      columns: ['Percent', 'Letter (A–F scale)', 'Letter (plus/minus)', 'GPA points (plus/minus)'],
      rows: lookupRows,
    },
  ];
  CORE_PAGES['letter-grade-calculator'].sections.unshift({
    heading: 'What grade is 75 percent?',
    paragraphs: [
      `A 75% is a ${p75std.letter} on the standard A–F scale and a ${p75pm.letter} on the plus/minus scale, which is ${p75pm.gpa.toFixed(1)} GPA points. Use the chart above to look up any other percentage from 60 to 100.`,
    ],
  });
  const t44 = calculateTestGrade({ earned: 44, possible: 60 });
  const t44pct = pct(t44.percent);
  CORE_PAGES['test-grade-calculator'].sections.unshift({
    heading: 'What grade is 44 out of 60?',
    paragraphs: [
      `44 out of 60 is ${t44pct}%, which is a ${percentToLetter(t44pct, SCALE_STANDARD).letter} on the standard A–F scale and a ${percentToLetter(t44pct, SCALE_PLUS_MINUS).letter} on the plus/minus scale. The same method works for any score: divide points earned by points possible and multiply by 100.`,
    ],
  });
}

// Competitor-gap revamps (2026-10-28): grade-curve and letter-grade pages
// get new FAQs here. NOTE: both pages render a long-form guide instead of
// entry.sections (ToolPage renders sections only for non-guide pages), so
// the matching methods content lives as visible guide blocks in
// toolGuideContent.ts — appending sections here would be invisible.
{
  CORE_PAGES['grade-curve-calculator'].faqs.push(
    {
      question: 'What is a cube-root grade curve?',
      answer:
        'A cube-root curve replaces each raw score with 100 times the cube root of the raw score divided by 100. On a 100-point test a raw 27 becomes about 64.6, a raw 64 becomes about 86.2 and a raw 8 becomes about 43.1. Low scores gain the most and the class spread narrows sharply, which suits a test that turned out far harder than intended.',
    },
    {
      question: 'Does a curve ever lower grades?',
      answer:
        'It can, depending on the method: a bell-curve rescaling can lower scores above the mean when the class average is already high, and a top-score method that rescales to the highest paper can lower nothing but also helps no one at the top. Most teachers cap a curve so no student’s score falls below the raw score — a curve that can only help is far easier to defend to a class.',
    },
    {
      question: 'Which curve method should a teacher choose?',
      answer:
        'Match the method to the problem. Flat points suit a uniformly hard test where everyone deserves the same lift. Top-score-to-100 suits a test where the best paper should define full marks. Square or cube root suits a test with many low scores that need lifting most. Target average suits a class that must land on a department average. Bell-curve rescaling suits a large class where the spread itself needs reshaping. Announce the method before the grades go out.',
    },
  );

  CORE_PAGES['letter-grade-calculator'].faqs.push(
    {
      question: 'Is 91 an A or an A minus?',
      answer:
        'On the standard A–F scale, 91 is an A, because A covers 90–100%. On the plus/minus scale, 91 is an A−, because A covers 93–96.99% and A− covers 90–92.99%. The percent is the same; only the scale changes the letter, so check which scale your school uses before reporting the grade.',
    },
    {
      question: 'Why do two schools give different letters for the same percent?',
      answer:
        'Because grading scales are local policy, not a universal standard. One school’s A starts at 90, another’s at 93 on a 7-point scale, and a third uses plus/minus splits inside every band. The percent is a fact; the letter is an interpretation of that fact under a particular school’s scale.',
    },
    {
      question: 'Does plus/minus help or hurt GPA?',
      answer:
        'It depends on where your percents fall. Plus/minus rewards scores at the top of each band — a 97 earns 4.0 where the standard scale also gives 4.0, but an 87 earns 3.3 instead of a flat 3.0 for a B — and it penalises scores at the bottom of a band, where a 90 earns 3.7 instead of 4.0. Over a whole transcript the two systems roughly balance, but individual terms can swing either way.',
    },
  );
}

export const TOOL_PAGES: Record<string, ToolPageEntry> = {
  ...CORE_PAGES,
  'average-grade-calculator': buildAverageGradePage(),
  'weighted-gpa-calculator': buildWeightedGpaPage(),
  'semester-gpa-calculator': buildSemesterGpaPage(),
  'cumulative-gpa-calculator': buildCumulativeGpaPage(),
  'for-schools': buildForSchoolsPage(),
  'blog': buildBlogIndexPage(),
  'blog/how-to-grade-30-tests-in-15-minutes': buildGradeFasterPost(),
  'blog/when-to-curve-grades': buildCurveGuidePost(),
  'blog/grading-systems-explained': buildGradingSystemsPost(),
  'blog/high-school-vs-college-gpa': buildHighSchoolVsCollegePost(),
  'weighted-vs-unweighted-gpa': buildWeightedVsUnweightedPage(),
  'how-many-questions-can-i-miss': buildMissCalculatorPage(),
  'grading-scale': buildGradingScaleIndexPage(),
  'gpa-to-letter-grade': buildGpaToLetterPage(),
  'letter-grade-to-gpa': buildLetterToGpaPage(),
  'high-school-gpa-calculator': buildHighSchoolGpaPage(),
  'college-gpa-calculator': buildCollegeGpaPage(),
  'final-grade-planner': buildFinalPlannerPage(),
  ...Object.fromEntries(COMPETITOR_GAP_PAGES.map((entry) => [entry.slug, entry] as const)),
  ...Object.fromEntries(buildCgpaUniversityPages().map((entry) => [entry.slug, entry] as const)),
  ...Object.fromEntries(GRADING_SCALE_SIZES.map((n) => {
    const entry = buildQuestionCountPage(n);
    return [entry.slug, entry] as const;
  })),
};

// Keyword-mapped sections, lookup tables and FAQs (SEO round 2026-10-26)
applySeoExtras(TOOL_PAGES);

// Exact primary keyword in title, H1 and first paragraph of every page (SEO round 2026-11-02)
applyOnPageSeo(TOOL_PAGES);

// Every page gets its own share card: public/og-cards/<slug with / replaced by ->-1200x630.png
for (const entry of Object.values(TOOL_PAGES)) {
  entry.ogImage = `${BASE_CANONICAL_ORIGIN}/og-cards/${entry.slug.replace(/\//g, '-')}-1200x630.png`;
}

export const TOOL_PAGE_LIST: ToolPageEntry[] = Object.values(TOOL_PAGES);

export function getToolPageByPath(rawPath: string): ToolPageEntry | undefined {
  const clean = (rawPath.split(/[?#]/)[0].replace(/\/+$/, '') || '/').toLowerCase();
  return TOOL_PAGE_LIST.find((p) => p.path.replace(/\/+$/, '') === clean);
}

export function toolPageCanonical(entry: ToolPageEntry): string {
  return `${BASE_CANONICAL_ORIGIN}${entry.path}`;
}
